import { authService } from "services/authService";
import { classMgmtService } from "services/admin/classMgmtService";
import type {
  ClassListItem,
  PaginatedResponse,
  SessionWithDetails,
} from "types/domain";
import type { TeacherAssignedClass } from "./types";

async function getPaged<T>(
  fetcher: () => Promise<PaginatedResponse<T>>
): Promise<T[]> {
  const res = await fetcher();
  return res.items;
}

async function getAllSessionFeed(): Promise<SessionWithDetails[]> {
  const items: SessionWithDetails[] = [];
  const limit = 200;
  let skip = 0;

  while (true) {
    const response = await classMgmtService.getSessionsFeed({ skip, limit });
    items.push(...response.items);
    if (response.items.length < limit || items.length >= response.total) {
      break;
    }
    skip += limit;
  }

  return items;
}

export const teachingClassService = {
  getMyAssignedClasses: async (): Promise<TeacherAssignedClass[]> => {
    const current = authService.getStoredUser() ?? (await authService.getMe());
    const meId = current.id;

    const [allClasses, feedSessions] = await Promise.all([
      getPaged(() => classMgmtService.getAll({ skip: 0, limit: 200 })),
      getAllSessionFeed(),
    ]);

    const managedClasses = new Map(
      (allClasses as ClassListItem[])
        .filter((classItem) =>
          classItem.managers?.some((manager) => manager.id === meId)
        )
        .map((classItem) => [classItem.id, classItem] as const)
    );

    const participantSessionsByClassId = new Map<
      string,
      TeacherAssignedClass["sessions"]
    >();
    feedSessions.forEach((session) => {
      if (!session.class_id) return;
      const existing = participantSessionsByClassId.get(session.class_id) ?? [];
      existing.push(session);
      participantSessionsByClassId.set(session.class_id, existing);
    });

    const managedClassIds = Array.from(managedClasses.keys());
    const fullManagedSessionsResults = await Promise.all(
      managedClassIds.map(
        async (classId) =>
          [classId, await classMgmtService.getSessions(classId)] as const
      )
    );
    const fullManagedSessionsByClassId = new Map(fullManagedSessionsResults);

    const assignedClassIds = new Set<string>([
      ...managedClassIds,
      ...participantSessionsByClassId.keys(),
    ]);
    const assignedClasses = (allClasses as ClassListItem[]).filter(
      (classItem) => assignedClassIds.has(classItem.id)
    );

    const enrollmentResults = await Promise.all(
      assignedClasses.map(
        async (classItem) =>
          [
            classItem.id,
            await classMgmtService.getEnrollments(classItem.id),
          ] as const
      )
    );
    const enrollmentsByClassId = new Map(enrollmentResults);

    // Fallbacks are i18n keys; callers render them through t().
    return assignedClasses.map((classItem) => ({
      ...classItem,
      course_title: classItem.class_code ?? "teaching.defaultClassTitle",
      course_description: null,
      center_name: classItem.center_name ?? "teaching.defaultCenterName",
      sessions:
        fullManagedSessionsByClassId.get(classItem.id) ??
        participantSessionsByClassId.get(classItem.id) ??
        [],
      enrollments: enrollmentsByClassId.get(classItem.id) ?? [],
    }));
  },
};
